import {
  resolveChamferFaceFaceMode,
  resolveOffsetFaceChamferMode,
} from './normalizeChamferFeatureDefinition';
import { hasAuthoritativeBlendSelectionRuntimeHint } from './hasAuthoritativeBlendEdgeRuntimeHint';

/**
 * 倒角草稿提交选择摘要。
 */
export interface ChamferDraftSubmissionItem {
  /** 选择项种类。 */
  kind: BlendSelectionKind;
  /** 稳定拓扑引用。 */
  persistentRef: string;
  /** 运行时权威拓扑提示。 */
  runtimeTopologyHint?: RuntimeTopologyHint;
}

/**
 * 倒角草稿提交校验输入。
 */
export interface ChamferDraftSubmissionValidationInput {
  /** 倒角定义。 */
  definition: ChamferFeatureDefinition;
  /** 上游实体对象 ID。 */
  upstreamBodyObjectId: string | null;
  /** 当前选择摘要。 */
  items: ChamferDraftSubmissionItem[];
}

/**
 * 判断数值是否为正数。
 *
 * @param value 输入数值。
 * @returns 是否为正数。
 */
function isPositiveNumber(value: number | undefined): boolean {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value > 0;
}

/**
 * 判断选择项是否携带可用于 worker 回绑的权威拓扑提示。
 *
 * @param item 选择摘要。
 * @returns 是否具备权威提示。
 */
function hasAuthoritativeRuntimeHint(item: ChamferDraftSubmissionItem): boolean {
  return hasAuthoritativeBlendSelectionRuntimeHint(item);
}

/**
 * 校验距离-距离倒角是否可提交。
 *
 * @param definition 倒角定义。
 * @returns 是否可提交。
 */
function canSubmitDistanceDistance(definition: ChamferFeatureDefinition): boolean {
  if (!isPositiveNumber(definition.distance)) {
    return false;
  }
  if (definition.symmetric !== false) {
    return true;
  }
  return isPositiveNumber(definition.distance2);
}

/**
 * 校验顶点倒角是否可提交。
 *
 * @param definition 倒角定义。
 * @param items 当前选择摘要。
 * @returns 是否可提交。
 */
function canSubmitVertexChamfer(
  definition: ChamferFeatureDefinition,
  items: ChamferDraftSubmissionItem[],
): boolean {
  if (items.length !== 1 || items[0]?.kind !== 'vertex') {
    return false;
  }
  if (definition.equalDistance !== false) {
    return isPositiveNumber(definition.distance);
  }
  return Array.isArray(definition.vertexDistances)
    && definition.vertexDistances.length === 3
    && definition.vertexDistances.every((distance) => isPositiveNumber(distance));
}

/**
 * 校验 Face Face 倒角是否可提交。
 *
 * @param definition 倒角定义。
 * @returns 是否可提交。
 */
function canSubmitFaceFaceChamfer(definition: ChamferFeatureDefinition): boolean {
  const faceSet1 = definition.faceSets?.find((faceSet) => faceSet.setId === 'faceSet1')?.items ?? [];
  const faceSet2 = definition.faceSets?.find((faceSet) => faceSet.setId === 'faceSet2')?.items ?? [];
  if (faceSet1.length === 0 || faceSet2.length === 0) {
    return false;
  }
  const faceFaceMode = resolveChamferFaceFaceMode(definition);
  if (faceFaceMode === 'chord-width') {
    return isPositiveNumber(definition.width);
  }
  if (!isPositiveNumber(definition.distance)) {
    return false;
  }
  if (faceFaceMode === 'symmetric') {
    return true;
  }
  if (faceFaceMode === 'asymmetric') {
    return isPositiveNumber(definition.distance2);
  }
  return false;
}

/**
 * 校验等距面倒角是否可提交。
 *
 * @param definition 倒角定义。
 * @param items 当前选择摘要。
 * @returns 是否可提交。
 */
function canSubmitOffsetFaceChamfer(
  definition: ChamferFeatureDefinition,
  items: ChamferDraftSubmissionItem[],
): boolean {
  const mode = items.some((item) => item.kind === 'face')
    ? 'symmetric'
    : resolveOffsetFaceChamferMode(definition);
  if (!items.every((item) => item.kind === 'edge' || item.kind === 'face')) {
    return false;
  }
  if (mode === 'asymmetric' && !items.every((item) => item.kind === 'edge')) {
    return false;
  }
  if (!items.every(hasAuthoritativeRuntimeHint) || !isPositiveNumber(definition.distance)) {
    return false;
  }
  return mode === 'symmetric' || isPositiveNumber(definition.distance2);
}

/**
 * 校验倒角草稿是否可提交。
 *
 * @param input 校验输入。
 * @returns 是否可提交。
 */
export function validateChamferDraftSubmission(
  input: ChamferDraftSubmissionValidationInput,
): boolean {
  const { definition, upstreamBodyObjectId, items } = input;
  if (!upstreamBodyObjectId || items.length === 0) {
    return false;
  }

  switch (definition.type) {
    case 'angle-distance':
      return items.every(hasAuthoritativeRuntimeHint)
        && isPositiveNumber(definition.distance)
        && typeof definition.angleDeg === 'number'
        && Number.isFinite(definition.angleDeg)
        && definition.angleDeg >= 0
        && definition.angleDeg <= 180;
    case 'distance-distance':
      return items.every(hasAuthoritativeRuntimeHint) && canSubmitDistanceDistance(definition);
    case 'vertex':
      return canSubmitVertexChamfer(definition, items);
    case 'offset-face':
      return canSubmitOffsetFaceChamfer(definition, items);
    case 'face-face':
      return canSubmitFaceFaceChamfer(definition);
    default:
      return false;
  }
}
